import React, { useEffect } from 'react';

type UseInfiniteScroll = (
    ref: React.RefObject<HTMLElement>,
    // eslint-disable-next-line no-use-before-define
    callback?: EventCallback,
    callbackOnMount?: boolean,
    scrollTriggerMargin?: number,
) => void;
type EventCallback = (evt?: Event) => void;

// The error margin in px we want to have for triggering infinite scroll
const SCROLL_TRIGGER_MARGIN = 5;

/**
 * @deprecated Please use <InfiniteScroll /> Component which uses IntersectionObserver (more efficient)
 *
 * Listen to scroll on an element and callback the passed in function if element is at bottom.
 *
 * @param  ref               A reference to the element on which you want to listen scroll event.
 * @param  [callback]        A callback function to call when the bottom of the reference element is reached.
 * @param  [callbackOnMount] A callback function to call when the component is mounted.
 * @param  [scrollTriggerMargin] The error margin in px we want to have for triggering infinite scroll
 *
 */
export const useInfiniteScroll: UseInfiniteScroll = (
    ref,
    callback,
    callbackOnMount = false,
    scrollTriggerMargin = SCROLL_TRIGGER_MARGIN,
) => {
    useEffect(() => {
        const { current } = ref;

        if (!callback || !current) {
            return undefined;
        }

        const isAtBottom = () =>
            Boolean(
                current && current.scrollHeight - (current.scrollTop + current.clientHeight) <= scrollTriggerMargin,
            );

        const onScroll = (e?: Event): void => {
            if (isAtBottom()) {
                callback(e);
            }
        };

        if (isAtBottom()) {
            onScroll();
        }

        current.addEventListener('scroll', onScroll);
        current.addEventListener('resize', onScroll);

        return () => {
            current.removeEventListener('scroll', onScroll);
            current.removeEventListener('resize', onScroll);
        };
    }, [ref, callback, scrollTriggerMargin]);

    useEffect(() => {
        if (callback && callbackOnMount) {
            callback();
        }
    }, [callback, callbackOnMount]);
};
